import React, { useState, useEffect } from 'react';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  UserPlusIcon,
  CogIcon,
  QrCodeIcon,
  ShareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import FieldManager from './FieldManager';
import useServices from '../hooks/useServices';

const LinkGenerator = () => {
  const services = useServices();
  const [copiedLink, setCopiedLink] = useState('');
  const [customCampaign, setCustomCampaign] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    email: '',
    phone: '',
    source: 'website',
    budget: '',
    notes: ''
  });

  const baseUrl = window.location.origin;

  const socialPlatforms = [
    {
      name: 'Instagram',
      icon: '📷',
      source: 'instagram',
      campaigns: ['instagram_story', 'instagram_post', 'instagram_bio', 'instagram_reel'],
      color: 'bg-pink-100 text-pink-800 border-pink-200'
    },
    {
      name: 'Facebook',
      icon: '📘',
      source: 'facebook',
      campaigns: ['facebook_post', 'facebook_story', 'facebook_ad', 'facebook_page'],
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      name: 'WhatsApp',
      icon: '💬',
      source: 'whatsapp',
      campaigns: ['whatsapp_status', 'whatsapp_business', 'whatsapp_group', 'whatsapp_broadcast'],
      color: 'bg-green-100 text-green-800 border-green-200'
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      source: 'linkedin',
      campaigns: ['linkedin_post', 'linkedin_article', 'linkedin_message', 'linkedin_profile'],
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    },
    {
      name: 'Twitter',
      icon: '🐦',
      source: 'twitter',
      campaigns: ['twitter_post', 'twitter_dm', 'twitter_bio', 'twitter_thread'],
      color: 'bg-sky-100 text-sky-800 border-sky-200'
    },
    {
      name: 'YouTube',
      icon: '📺',
      source: 'youtube',
      campaigns: ['youtube_description', 'youtube_comment', 'youtube_community', 'youtube_shorts'],
      color: 'bg-red-100 text-red-800 border-red-200'
    },
    {
      name: 'Website',
      icon: '🌐',
      source: 'website',
      campaigns: ['website_banner', 'website_popup', 'website_footer', 'website_blog'],
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    }
  ];

  const generateLink = (source, campaign) => {
    const finalCampaign = campaign === 'custom' ? customCampaign : campaign;
    const serviceParam = selectedService ? `&service=${encodeURIComponent(selectedService)}` : '';
    return `${baseUrl}/capture?source=${source}&campaign=${finalCampaign}${serviceParam}&utm_source=${source}&utm_campaign=${finalCampaign}`;
  };

  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      setTimeout(() => setCopiedLink(''), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const generateQRCode = (link) => {
    // Using a free QR code API
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(link)}`;
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Lead added successfully!');
        setFormData({
          title: '',
          name: '',
          email: '',
          phone: '',
          source: 'website',
          budget: '',
          notes: ''
        });
        setShowLeadForm(false);
      } else {
        alert('Error adding lead. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error adding lead. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <LinkIcon className="h-6 w-6 mr-2 text-blue-600" />
            Lead Capture Links Generator
          </h2>
          <p className="text-gray-600">
            Generate custom lead capture links for your social media platforms and marketing campaigns
          </p>
        </div>
      </div>


      {/* Link Generator Content */}

      {/* Custom Campaign Input */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Custom Campaign Name (Optional)
        </label>
        <input
          type="text"
          value={customCampaign}
          onChange={(e) => setCustomCampaign(e.target.value)}
          placeholder="e.g., diwali_offer, new_year_sale"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          This campaign name will help you track lead sources in your anna
        </p>
      </div>

      {/* Platform Links */}
      <div className="space-y-6">
        {socialPlatforms.map((platform) => (
          <div key={platform.source} className={`border-2 rounded-lg p-4 ${platform.color}`}>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <span className="text-2xl mr-2">{platform.icon}</span>
              {platform.name}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {platform.campaigns.map((campaign) => {
                const link = generateLink(platform.source, campaign);
                const isCustom = campaign === 'custom';

                return (
                  <div key={campaign} className="bg-white rounded-md p-3 border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {campaign.replace('_', ' ')}
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => copyToClipboard(link)}
                          className={`p-1 rounded transition-colors ${copiedLink === link
                              ? 'text-green-600 bg-green-100'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                          title="Copy Link"
                        >
                          {copiedLink === link ? (
                            <CheckIcon className="h-4 w-4" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => window.open(generateQRCode(link), '_blank')}
                          className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          title="Generate QR Code"
                        >
                          <QrCodeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded break-all">
                      {link}
                    </div>
                  </div>
                );
              })}

              {/* Custom Campaign Option */}
              {customCampaign && (
                <div className="bg-white rounded-md p-3 border border-dashed border-gray-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Custom: {customCampaign}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(generateLink(platform.source, 'custom'))}
                        className={`p-1 rounded transition-colors ${copiedLink === generateLink(platform.source, 'custom')
                            ? 'text-green-600 bg-green-100'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        title="Copy Link"
                      >
                        {copiedLink === generateLink(platform.source, 'custom') ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => window.open(generateQRCode(generateLink(platform.source, 'custom')), '_blank')}
                        className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        title="Generate QR Code"
                      >
                        <QrCodeIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded break-all">
                    {generateLink(platform.source, 'custom')}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
          <ShareIcon className="h-5 w-5 mr-2" />
          How to Use:
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Copy the link for your desired platform and campaign</li>
          <li>• Paste the link in your social media posts, bio, or stories</li>
          <li>• When clients click the link, leads will be automatically captured</li>
          <li>• <strong>🎯 Lead Title/Purpose will be automatically set based on the form selection</strong></li>
          <li>• Track lead sources and campaign performance in your anna</li>
          <li>• Generate QR codes for print materials and offline marketing</li>
        </ul>
      </div>

      {/* Title/Purpose Integration Info */}
      <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
        <h4 className="font-semibold text-green-900 mb-2 flex items-center">
          🎯 Lead Title Integration:
        </h4>
        <p className="text-sm text-green-800">
          जब customer आपके link से form भरेगा, तो <strong>"Enquiry Purpose"</strong> field में से select करेगा कि उसे क्या service चाहिए।
          यह automatically <strong>Lead Title</strong> बन जाएगा और आपके anna में prominently display होगा।
        </p>
      </div>

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 md:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-0 md:mx-4 max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                  Add New Lead
                </h3>
                <button
                  onClick={() => setShowLeadForm(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 md:p-1.5"
                >
                  <XMarkIcon className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 gap-4 md:gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2 bg-blue-50 p-3 md:p-4 rounded-lg border-2 border-blue-200">
                    <label htmlFor="title" className="flex flex-col md:flex-row md:items-center text-sm md:text-base font-bold text-blue-800 mb-2">
                      <span>Lead Title / Purpose *</span>
                      <span className="text-xs md:text-sm font-normal text-blue-600 md:ml-2 mt-1 md:mt-0">(यह lead किस काम के लिए आई है?)</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-lg font-medium"
                      value={formData.title}
                      onChange={handleFormChange}
                      required
                      placeholder="जैसे: Website Development, Digital Marketing, etc."
                    />
                    <p className="text-xs md:text-sm text-blue-600 mt-1.5 md:mt-2">
                      यह field बहुत important है - इससे पता चलेगा कि customer को क्या service चाहिए
                    </p>
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      placeholder="Enter full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="source" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Lead Source
                    </label>
                    <select
                      id="source"
                      name="source"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.source}
                      onChange={handleFormChange}
                    >
                      <option value="website">Website</option>
                      <option value="referral">Referral</option>
                      <option value="social_media">Social Media</option>
                      <option value="email_campaign">Email Campaign</option>
                      <option value="phone_call">Phone Call</option>
                      <option value="trade_show">Trade Show</option>
                      <option value="advertisement">Advertisement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="budget" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Budget Range
                    </label>
                    <input
                      type="text"
                      name="budget"
                      id="budget"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.budget}
                      onChange={handleFormChange}
                      placeholder="e.g., ₹1,00,000 - ₹2,50,000 or ₹75,000"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="notes" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      id="notes"
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.notes}
                      onChange={handleFormChange}
                      placeholder="Add any additional notes or comments..."
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 md:pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowLeadForm(false)}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add Lead
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {copiedLink && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
          ✅ Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default LinkGenerator;
